import type {
  ConversationStatus,
  LeadStatus,
  ChannelStatus,
  AgentStatus,
  CampaignStatus,
  ChannelType,
  Role,
  EmployeeRole,
  JobStatus,
} from '@conversia/shared-types';
import type { BadgeProps } from '@/components/ui/Badge';

type Variant = NonNullable<BadgeProps['variant']>;
export interface BadgeMeta {
  label: string;
  variant: Variant;
}

export function statusBadge(status: ConversationStatus): BadgeMeta {
  switch (status) {
    case 'NEW':
      return { label: 'Nueva', variant: 'info' };
    case 'ACTIVE':
      return { label: 'Activa', variant: 'success' };
    case 'WAITING_HUMAN':
      return { label: 'Atención', variant: 'warning' };
    case 'CLOSED':
      return { label: 'Cerrada', variant: 'default' };
    case 'ARCHIVED':
      return { label: 'Archivada', variant: 'default' };
  }
}

/** Etiqueta de la temperatura comercial del lead (pestañas del inbox). */
export function leadStatusBadge(status: LeadStatus): BadgeMeta {
  switch (status) {
    case 'CALIENTE':
      return { label: 'Caliente', variant: 'danger' };
    case 'TIBIO':
      return { label: 'Tibio', variant: 'warning' };
    case 'FRIO':
      return { label: 'Frío', variant: 'info' };
    case 'CERRADO':
      return { label: 'Cerrado', variant: 'success' };
    case 'NO_CERRADO':
      return { label: 'No cerrado', variant: 'default' };
  }
}

export function channelStatusBadge(status: ChannelStatus): BadgeMeta {
  switch (status) {
    case 'CONNECTED':
      return { label: 'Conectado', variant: 'success' };
    case 'CONNECTING':
      return { label: 'Conectando', variant: 'warning' };
    case 'RECONNECTING':
      return { label: 'Reconectando', variant: 'warning' };
    case 'QR_REQUIRED':
      return { label: 'Escanear QR', variant: 'info' };
    case 'BANNED':
      return { label: 'Bloqueado', variant: 'danger' };
    case 'ERROR':
      return { label: 'Error', variant: 'danger' };
    case 'DISCONNECTED':
    default:
      return { label: 'Desconectado', variant: 'default' };
  }
}

/**
 * Etiqueta de calentamiento (warmup) de un canal de WhatsApp. Devuelve `null`
 * si el número no está en warmup; si no, el texto a mostrar y si ya terminó.
 * El día y el tope se calculan en el cliente a partir de `warmupStartedAt`.
 */
export function channelWarmupLabel(channel: {
  warmupStartedAt?: string | null;
  warmupCompletedAt?: string | null;
  dailyOutboundCount?: number;
}): { text: string; badge: string; tooltip: string; done: boolean } | null {
  if (!channel.warmupStartedAt) return null;
  if (channel.warmupCompletedAt)
    return { text: 'Warmup completado', badge: 'Warmup ✓', tooltip: 'Calentamiento completado', done: true };
  const startedMs = new Date(channel.warmupStartedAt).getTime();
  if (Number.isNaN(startedMs)) return null;
  const day = Math.floor((Date.now() - startedMs) / 86_400_000) + 1;
  if (day > 7)
    return { text: 'Warmup completado', badge: 'Warmup ✓', tooltip: 'Calentamiento completado', done: true };
  const limit = day < 3 ? 20 : 50;
  const count = channel.dailyOutboundCount ?? 0;
  return {
    text: `Calentamiento · Día ${day}/7 · ${count}/${limit} hoy`,
    badge: `Warmup Día ${day}/7`,
    tooltip: `Día ${day}/7 · ${count}/${limit} msgs hoy`,
    done: false,
  };
}

export function agentStatusBadge(status: AgentStatus): BadgeMeta {
  switch (status) {
    case 'ACTIVE':
      return { label: 'Activo', variant: 'success' };
    case 'PAUSED':
      return { label: 'Pausado', variant: 'warning' };
    case 'ARCHIVED':
      return { label: 'Archivado', variant: 'default' };
  }
}

export function campaignStatusBadge(status: CampaignStatus): BadgeMeta {
  switch (status) {
    case 'DRAFT':
      return { label: 'Borrador', variant: 'default' };
    case 'SCHEDULED':
      return { label: 'Programada', variant: 'info' };
    case 'RUNNING':
      return { label: 'En curso', variant: 'success' };
    case 'PAUSED':
      return { label: 'Pausada', variant: 'warning' };
    case 'COMPLETED':
      return { label: 'Completada', variant: 'brand' };
    case 'CANCELLED':
      return { label: 'Cancelada', variant: 'danger' };
  }
}

export const channelTypeLabel: Record<ChannelType, string> = {
  WHATSAPP: 'WhatsApp',
  INSTAGRAM: 'Instagram',
  MESSENGER: 'Messenger',
  TELEGRAM: 'Telegram',
  EMAIL: 'Email',
};

export const roleLabel: Record<Role, string> = {
  OWNER: 'Dueño del sistema',
  ADMIN: 'Administrador',
  AGENT: 'Agente',
  VIEWER: 'Lector',
  TECNICO: 'Técnico',
};

/** Etiqueta del rol/función del empleado (capa de operaciones). */
export const employeeRoleLabel: Record<EmployeeRole, string> = {
  ADMIN: 'Administrador',
  TECNICO: 'Técnico',
  SOPORTE: 'Soporte',
};

/** Sentimiento de la respuesta de encuesta (centro del negocio). */
export function encuestaBadge(sentiment: 'good' | 'bad' | 'neutral'): BadgeMeta {
  switch (sentiment) {
    case 'good':
      return { label: 'Contento', variant: 'success' };
    case 'bad':
      return { label: 'Queja', variant: 'danger' };
    case 'neutral':
      return { label: 'Respondió', variant: 'default' };
  }
}

/** Estado de un trabajo/servicio (capa de operaciones). */
export function jobStatusBadge(status: JobStatus): BadgeMeta {
  switch (status) {
    case 'ASIGNADO':
      return { label: 'Asignado', variant: 'info' };
    case 'EN_SITIO':
      return { label: 'En sitio', variant: 'warning' };
    // Portado de silvai-chat (S-01): el técnico cerró en sitio pero falta que
    // supervisión complete monto, forma de pago y vendedor. NO puede verse como
    // "listo" (por eso no es 'success'): es trabajo pendiente para el panel.
    case 'PENDIENTE_VALIDACION':
      return { label: 'Por validar', variant: 'info' };
    case 'COMPLETADO':
      return { label: 'Completado', variant: 'success' };
    case 'NO_COMPLETADO':
      return { label: 'No completado', variant: 'danger' };
    case 'CANCELADO':
      return { label: 'Cancelado', variant: 'default' };
  }
}
